import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import Restaurant from './restaurant'
import ProfilePage from './profilePage'
import GroupDining from './group'
import { apiFetch } from '../lib/api'
import { usePageSeo } from '../lib/seo'
import {
  getCityBySlug,
  getEventCitySeoPath,
  getEventSeoPath,
  getRestaurantCitySeoPath,
  getProfileSeoPath,
  getRestaurantSeoPath,
  slugifySeoSegment,
  titleCaseFromSlug,
} from '../lib/seoRoutes'

function getCityDisplayName(citySlug, fallback = '') {
  return getCityBySlug(citySlug)?.name || titleCaseFromSlug(citySlug) || fallback
}

function formatDateLabel(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

function PageShell({ eyebrow, title, description, children }) {
  return (
    <main className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-100 px-4 py-8 sm:px-8">
      <article className="mx-auto w-full max-w-6xl rounded-3xl border border-orange-100 bg-white/85 p-5 shadow-sm backdrop-blur sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600">{eyebrow}</p>
        <h1 className="mt-3 title-font text-3xl font-extrabold text-gray-900 sm:text-5xl">{title}</h1>
        <p className="mt-4 max-w-3xl text-sm leading-relaxed text-gray-700 sm:text-base">{description}</p>
        <div className="mt-8">{children}</div>
      </article>
    </main>
  )
}

export function RestaurantSeoRoutePage() {
  const { city: citySlug = '', name: restaurantSlug = '' } = useParams()
  const cityName = useMemo(() => getCityDisplayName(citySlug, 'Bengaluru'), [citySlug])
  const canonicalPath = useMemo(
    () => (restaurantSlug ? getRestaurantSeoPath(cityName, restaurantSlug) : getRestaurantCitySeoPath(cityName)),
    [cityName, restaurantSlug],
  )
  const currentYear = new Date().getFullYear()
  const pageTitle = `Best Restaurants in ${cityName} in ${currentYear}`
  const pageDescription = restaurantSlug
    ? `Discover the best restaurants in ${cityName} in ${currentYear}, and open the restaurant page for live details, reviews, and availability.`
    : `Browse all restaurants in ${cityName} in ${currentYear}. Compare ratings, opening status, and meetup-friendly spots on Co-Eater.`

  usePageSeo({
    title: pageTitle,
    description: pageDescription,
    keywords: `best restaurants in ${cityName}, restaurants in ${cityName}, social dining ${cityName}, meetup restaurants`,
    pathname: canonicalPath,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: pageTitle,
      url: `https://coeater.com${canonicalPath}`,
      description: pageDescription,
    },
  })

  return (
    <PageShell
      eyebrow="Restaurants"
      title={pageTitle}
      description={pageDescription}
    >
      <Restaurant
        currentUser={null}
        gpsCity={cityName}
        cityOverride={cityName}
        restaurantNameOverride={restaurantSlug}
      />
    </PageShell>
  )
}

export function ProfileSeoRoutePage() {
  const { name: profileSlug = '' } = useParams()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true

    async function loadProfile() {
      setLoading(true)
      try {
        const res = await apiFetch(`/profile/${profileSlug}`)
        if (!res.ok) throw new Error('Failed to load profile')
        const data = await res.json()
        if (active) setProfile(data.profile || null)
      } catch {
        if (active) setProfile(null)
      } finally {
        if (active) setLoading(false)
      }
    }

    if (profileSlug) {
      loadProfile()
    } else {
      setProfile(null)
      setLoading(false)
    }

    return () => {
      active = false
    }
  }, [profileSlug])

  const profileName = profile?.name || titleCaseFromSlug(profileSlug) || 'Profile'
  const cityName = profile?.location?.city || 'Mumbai'
  const canonicalPath = useMemo(() => getProfileSeoPath(profileName), [profileName])

  usePageSeo({
    title: `Dine out in ${cityName} with ${profileName}`,
    description: `Meet ${profileName} in ${cityName} through Co-Eater. View preferences, stories, and recent posts before you plan a meal together.`,
    keywords: `dine out in ${cityName}, ${profileName}, social dining profile, meet people for dinner`,
    pathname: canonicalPath,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ProfilePage',
      name: profileName,
      url: `https://coeater.com${canonicalPath}`,
      description: `Dine out in ${cityName} with ${profileName}.`,
    },
  })

  if (loading) {
    return (
      <PageShell
        eyebrow="Profile"
        title={`Dine out in ${cityName} with ${profileName}`}
        description="Loading profile details..."
      >
        <div className="animate-pulse rounded-3xl border border-white/70 bg-white/80 p-6 shadow-xl">
          <div className="h-5 w-48 rounded bg-gray-200" />
          <div className="mt-4 h-40 rounded-3xl bg-gray-200" />
        </div>
      </PageShell>
    )
  }

  if (!profile) {
    return (
      <PageShell
        eyebrow="Profile"
        title={`Dine out in ${cityName} with ${profileName}`}
        description="We could not find that profile."
      >
        <div className="rounded-3xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          This profile is not available right now.
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell
      eyebrow="Profile"
      title={`Dine out in ${cityName} with ${profileName}`}
      description={`Meet ${profileName} in ${cityName} through Co-Eater. View preferences, stories, and recent posts before you plan a meal together.`}
    >
      <ProfilePage currentUser={null} userId={profile.id} />
    </PageShell>
  )
}

export function EventSeoRoutePage() {
  const { city: citySlug = '', eventName: eventSlug = '' } = useParams()
  const cityName = useMemo(() => getCityDisplayName(citySlug, 'Nashik'), [citySlug])
  const canonicalPath = useMemo(
    () => (eventSlug ? getEventSeoPath(cityName, eventSlug) : getEventCitySeoPath(cityName)),
    [cityName, eventSlug],
  )
  const [resolvedEventId, setResolvedEventId] = useState(null)
  const [eventData, setEventData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true

    async function loadEvent() {
      setLoading(true)
      try {
        const query = new URLSearchParams({ city: cityName, limit: '500' })
        const res = await apiFetch(`/group?${query.toString()}`)
        if (!res.ok) throw new Error('Failed to load group events')
        const data = await res.json()
        const events = Array.isArray(data.events) ? data.events : []
        const targetSlug = slugifySeoSegment(eventSlug)
        const match = events.find((event) => slugifySeoSegment(event.tableName) === targetSlug)
        if (active) {
          setResolvedEventId(match?.id || null)
          setEventData(match || null)
        }
      } catch {
        if (active) {
          setResolvedEventId(null)
          setEventData(null)
        }
      } finally {
        if (active) setLoading(false)
      }
    }

    if (cityName && eventSlug) {
      loadEvent()
    } else {
      setResolvedEventId(null)
      setEventData(null)
      setLoading(false)
    }

    return () => {
      active = false
    }
  }, [cityName, eventSlug])

  const eventDateLabel = formatDateLabel(eventData?.dateTime)
  const pageTitle = eventSlug
    ? (eventDateLabel
      ? `Things to do in ${eventData?.city || cityName} on ${eventDateLabel}`
      : `Things to do in ${cityName} on ${titleCaseFromSlug(eventSlug) || 'this event'}`)
    : `Things to do in ${cityName}`
  const pageDescription = eventSlug
    ? (eventData
      ? `Join ${eventData.tableName} in ${eventData.city || cityName} on ${eventDateLabel || 'the selected date'}. Find people, seats, and dining details on Co-Eater.`
      : `Discover things to do in ${cityName} on Co-Eater. Explore group dining events, open seats, and social meal plans.`)
    : `Explore things to do in ${cityName}. Browse group dining events, open seats, and social meal plans on Co-Eater.`

  usePageSeo({
    title: pageTitle,
    description: pageDescription,
    keywords: `things to do in ${cityName}, social dining ${cityName}, group dining events, meal meetups`,
    pathname: canonicalPath,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: eventData?.tableName || pageTitle,
      url: `https://coeater.com${canonicalPath}`,
      description: eventData
        ? `Join ${eventData.tableName} in ${eventData.city || cityName}.`
        : `Discover group dining events in ${cityName}.`,
      startDate: eventData?.dateTime || undefined,
    },
  })

  return (
    <PageShell
      eyebrow="Group Dining"
      title={pageTitle}
      description={eventSlug
        ? (eventData
          ? `Join ${eventData.tableName} in ${eventData.city || cityName} on ${eventDateLabel || 'the selected date'}.`
          : `Explore social dining events in ${cityName}.`)
        : `Browse all social dining events in ${cityName}.`}
    >
      {loading && (
        <div className="animate-pulse rounded-3xl border border-white/70 bg-white/80 p-6 shadow-xl">
          <div className="h-5 w-48 rounded bg-gray-200" />
          <div className="mt-4 h-40 rounded-3xl bg-gray-200" />
        </div>
      )}
      <GroupDining
        currentUser={null}
        gpsCity={cityName}
        cityOverride={cityName}
        socket={null}
        onLogin={() => {}}
        onOpenProfile={() => {}}
        sharedEventId={resolvedEventId}
      />
    </PageShell>
  )
}
